import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { queryKeys } from '@/lib/queryKeys';
import { notifyError, notifySuccess } from '@/lib/useMutationToast';

/**
 * `/echipa` (team) hooks — Plan 3e Task 2. Server side shipped in Task 1
 * (server/src/routes/team.ts): org-scoped, `GET /echipa/members` and
 * `GET /echipa/invitations` are readable by any org member; every write is
 * gated server-side by `requireOrgAdmin` (403 for a non-admin). No server
 * changes here — types below are local to the client since `@lyra/shared`
 * doesn't define Member/Invitation shapes yet.
 */

export interface TeamMember {
  memberId: string;
  userId: string;
  name: string;
  email: string;
  role: 'owner' | 'admin' | 'member';
  /** The `member.permissions` column has no NOT NULL constraint (see
   *  `auth-schema.ts`) — the server's own `hasPermission`/`GET /me` guard
   *  it with `?? []` (`services/permissions.ts`, `routes/me.ts`), but
   *  `GET /echipa/members` passes the raw column through unguarded, so the
   *  client must treat it as nullable too. */
  permissions: string[] | null;
  createdAt: string;
}

export interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
}

/** `GET /echipa/members` — verified against `team.ts`'s `app.get('/echipa/members', ...)`:
 *  no admin gate, returns `{memberId,userId,name,email,role,permissions,createdAt}[]`. */
/** `enabled` lets a caller that only *conditionally* needs the roster (the shell's
 *  LowBalanceBanner, which names the admin to contact) avoid firing this query on
 *  every page for every user. Defaults to on, so existing callers are unchanged. */
export function useMembers(enabled: boolean = true) {
  return useQuery<TeamMember[]>({
    queryKey: queryKeys.members,
    queryFn: () => apiFetch<TeamMember[]>('/echipa/members'),
    enabled,
  });
}

function useInvalidateMembers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.members });
}

/** `DELETE /echipa/members/:memberId` — verified against `team.ts`: admin-gated,
 *  204 on success; 400 on self-removal or last-owner removal, 404 cross-org.
 *  The page pre-empts both 400 cases in the UI, but the server re-checks
 *  regardless (Romanian message left to propagate as an `ApiClientError`). */
export function useRemoveMember() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: (memberId: string) => apiFetch<void>(`/echipa/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Membrul a fost eliminat.');
    },
    onError: (error) => notifyError(error, 'Eliminarea membrului a eșuat.'),
  });
}

/** `PATCH /echipa/:memberId/permissions {permissions}` — the pre-existing
 *  endpoint (predates Task 1); admin-gated, returns `{id,permissions}`. Caller
 *  passes the member's full next `permissions` array (current ± 'nomenclator.edit'). */
export function useSetMemberPermission() {
  const invalidate = useInvalidateMembers();
  return useMutation({
    mutationFn: ({ memberId, permissions }: { memberId: string; permissions: string[] }) =>
      apiFetch<{ id: string; permissions: string[] }>(`/echipa/${memberId}/permissions`, {
        method: 'PATCH',
        body: JSON.stringify({ permissions }),
      }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Permisiunile membrului au fost actualizate.');
    },
    onError: (error) => notifyError(error, 'Actualizarea permisiunilor a eșuat.'),
  });
}

/** `GET /echipa/invitations` — verified against `team.ts`: no admin gate
 *  (any org member may read), returns pending, non-expired invitations only:
 *  `{id,email,role,expiresAt,createdAt}[]`. */
export function useInvitations() {
  return useQuery<TeamInvitation[]>({
    queryKey: queryKeys.invitations,
    queryFn: () => apiFetch<TeamInvitation[]>('/echipa/invitations'),
  });
}

function useInvalidateInvitations() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: queryKeys.invitations });
}

/** `POST /echipa/invitations {email}` — verified against `team.ts`: admin-gated,
 *  201 `{id,email,link}` on success; 409 when the email already belongs to a
 *  member or has a pending invitation. The 409 `ApiClientError` is left to
 *  propagate — the page shows its own fixed inline message for it. */
export function useInvite() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<{ id: string; email: string; link: string }>('/echipa/invitations', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Invitația a fost trimisă.');
    },
    onError: (error) => notifyError(error, 'Trimiterea invitației a eșuat.'),
  });
}

/** `DELETE /echipa/invitations/:id` — verified against `team.ts`: admin-gated,
 *  204 (soft-cancels — sets `status: 'canceled'`; the list query already
 *  filters to `status = 'pending'`, so a cancelled invitation drops out on
 *  the next fetch). */
export function useCancelInvitation() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/echipa/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Invitația a fost anulată.');
    },
    onError: (error) => notifyError(error, 'Anularea invitației a eșuat.'),
  });
}

/** `POST /echipa/invitations/:id/resend` — verified against `team.ts`:
 *  admin-gated, refreshes the expiry and returns a fresh `{id,link}` — the
 *  page displays this returned link directly (not re-derived), since it's
 *  the only place the full invitation link is exposed after creation. */
export function useResendInvitation() {
  const invalidate = useInvalidateInvitations();
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ id: string; link: string }>(`/echipa/invitations/${id}/resend`, { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      notifySuccess('Invitația a fost retrimisă.');
    },
    onError: (error) => notifyError(error, 'Retrimiterea invitației a eșuat.'),
  });
}
